import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { useT, useLang, itemHasLocale } from '../lib/i18n';
import { normalizeRichText } from '../lib/richText';
import { checkoutAPI } from '../lib/api';
import { BACKEND_URL as API } from '../lib/config';
import { LucideIcon } from './AurexSections';

// ─────────────────────────────────────────────────────────────────────────
// ModernServices — the «Moderno» theme Services section (s139, approved by
// Anthony: "Expanding Panels"). Photo panels side by side; the pointed/focused
// one opens with number, icon, title, short description, "Read more" (service
// page or external link) and, when the service has a price, price + Stripe
// checkout. The rest stay narrow with a vertical title. Phones: stacked, tap
// to open. Header (eyebrow/title/subtitle/CTA) = CMS → Section Manager →
// Services — Section Configuration. A service without image gets a brand
// gradient. Colours come only from theme tokens.
// ─────────────────────────────────────────────────────────────────────────

const SERIF = "'Playfair Display', Georgia, serif";
const img = (src) => (src && src.startsWith('/api') ? `${API}${src}` : src);
const plain = (html) => String(html || '').replace(/<[^>]*>/g, ' ').replace(/&nbsp;| /g, ' ').replace(/\s+/g, ' ').trim();

const CSS = `
.msv-panel{transition:flex-grow 520ms cubic-bezier(0.77,0,0.175,1)}
.msv-open-in{opacity:0;transform:translateY(10px);transition:opacity 360ms cubic-bezier(0.23,1,0.32,1) 160ms,transform 360ms cubic-bezier(0.23,1,0.32,1) 160ms}
.msv-panel[data-open="1"] .msv-open-in{opacity:1;transform:none}
.msv-vert{transition:opacity 200ms ease}
.msv-panel[data-open="1"] .msv-vert{opacity:0}
.msv-link{display:inline-flex;align-items:center;gap:.4rem;font-weight:700;font-size:.875rem;color:var(--color-accent,#ff9c1b)}
.msv-link .msv-arrow{transition:transform 200ms cubic-bezier(0.23,1,0.32,1)}
@media (hover:hover) and (pointer:fine){.msv-link:hover .msv-arrow{transform:translateX(3px)}}
.msv-buy{display:inline-flex;align-items:center;gap:.5rem;font-weight:600;font-size:.8125rem;padding:.6rem 1.1rem;border-radius:2px;background:var(--color-accent,#ff9c1b);color:var(--accent-ink,#0f172a);transition:transform 160ms ease-out,background-color 220ms ease}
.msv-buy:active{transform:scale(.97)}
.msv-cta{display:inline-flex;align-items:center;gap:.55rem;font-weight:600;font-size:.875rem;padding:.95rem 1.6rem;border-radius:2px;background:var(--color-accent,#ff9c1b);color:var(--accent-ink,#0f172a);transition:transform 160ms ease-out,background-color 220ms ease}
.msv-cta:active{transform:scale(.97)}
.msv-desc p{margin:0}
@media (prefers-reduced-motion:reduce){.msv-panel{transition:none}.msv-open-in{transition:opacity 200ms ease;transform:none}}
`;

function ServiceLink({ s, children, className }) {
  if (s.external_url) {
    return <a href={s.external_url} target={s.open_in_new_tab ? '_blank' : '_self'} rel="noreferrer" className={className} data-testid={`service-link-${s.id}`}>{children}</a>;
  }
  return <Link to={`/service/${s.id}`} className={className} data-testid={`service-link-${s.id}`}>{children}</Link>;
}

async function checkout(s) {
  if (!s.stripe_price_id) { toast.info('No pricing configured'); return; }
  try {
    const r = await checkoutAPI.create({ price_id: s.stripe_price_id, service_name: s.title, amount: s.price });
    if (r.data.url) window.location.href = r.data.url;
  } catch { toast.error('Checkout error'); }
}

function PanelBg({ s }) {
  const src = img(s.image);
  return (
    <>
      {src
        ? <img src={src} alt="" className="absolute inset-0 w-full h-full object-cover" />
        : <span className="absolute inset-0" style={{ background: 'linear-gradient(150deg, color-mix(in srgb, var(--color-accent, #ff9c1b) 55%, #fff), var(--color-accent, #ff9c1b) 55%, color-mix(in srgb, var(--color-accent, #ff9c1b) 70%, #000))' }} />}
      {/* Even translucent veil over the whole photo + a deeper fade where the
          text sits, so copy stays legible on busy pictures (Anthony, s141). */}
      <span className="absolute inset-0" style={{ background: 'rgba(12,13,16,.42)' }} />
      <span className="absolute inset-0" style={{ background: 'linear-gradient(0deg, rgba(12,13,16,.82) 0%, rgba(12,13,16,.5) 45%, rgba(12,13,16,0) 78%)' }} />
    </>
  );
}

function OpenContent({ s, n }) {
  const tt = useT();
  const desc = tt(s.short_description) || tt(s.description);
  return (
    <div className="msv-open-in relative flex flex-col gap-3 max-w-md">
      <div className="flex items-center gap-3">
        <span className="text-sm font-semibold" style={{ fontFamily: SERIF, color: 'var(--color-accent, #ff9c1b)', fontVariantNumeric: 'tabular-nums' }}>{String(n).padStart(2, '0')}</span>
        {s.icon && <span className="text-white/80"><LucideIcon name={s.icon} className="w-5 h-5" /></span>}
      </div>
      <h3 className="text-white font-bold" style={{ fontFamily: SERIF, fontSize: 'clamp(1.6rem, 2.4vw, 2.25rem)', lineHeight: 1.1 }}>{tt(s.title)}</h3>
      {plain(desc) && <div className="msv-desc text-sm md:text-base leading-relaxed" style={{ color: 'rgba(255,255,255,.85)' }} dangerouslySetInnerHTML={{ __html: normalizeRichText(desc) }} />}
      <div className="flex flex-wrap items-center gap-4 pt-1">
        <ServiceLink s={s} className="msv-link mx-link mx-dark">Read more <ArrowRight className="w-4 h-4 msv-arrow" /></ServiceLink>
        {s.price > 0 && (
          <>
            <span className="text-white font-bold" style={{ fontVariantNumeric: 'tabular-nums' }}>${s.price}</span>
            <button type="button" onClick={() => checkout(s)} className="msv-buy mx-btn mx-btn--primary mx-dark" data-testid={`service-buy-${s.id}`}>Get Started</button>
          </>
        )}
      </div>
    </div>
  );
}

export default function ModernServices({ services, cmsConfig = {} }) {
  const tt = useT();
  const { lang } = useLang();
  const list = (services || []).filter(s => itemHasLocale(s.title, lang));
  const [active, setActive] = useState(0);
  if (!list.length) return null;
  const open = Math.min(active, list.length - 1);

  const eyebrow = tt(cmsConfig.eyebrow);
  const title = tt(cmsConfig.title) || 'Our Services';
  const subtitle = tt(cmsConfig.subtitle);
  const ctaText = tt(cmsConfig.cta_text);
  const ctaUrl = tt(cmsConfig.cta_url);

  return (
    <section id="services" data-testid="services-section" className="relative bg-white">
      <style>{CSS}</style>
      <div className="max-w-[1440px] mx-auto px-5 sm:px-6 lg:px-[clamp(24px,5vw,80px)] py-20 lg:py-28">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10 lg:mb-14">
          <div className="flex flex-col gap-4 max-w-2xl">
            {eyebrow && (
              <div className="inline-flex items-center gap-3 text-[11px] font-bold uppercase" style={{ letterSpacing: '.2em', color: 'var(--accent-strong, #b86500)' }}>
                <span style={{ width: 32, height: 2, background: 'var(--color-accent, #ff9c1b)' }} />{eyebrow}
              </div>
            )}
            <h2 className="font-bold" style={{ fontFamily: SERIF, fontSize: 'clamp(2rem, 3.4vw, 3rem)', lineHeight: 1.08, color: 'var(--color-heading, #1a2332)', textWrap: 'balance' }} data-testid="services-title">{title}</h2>
            {subtitle && <p className="text-base md:text-lg leading-relaxed" style={{ color: 'var(--color-body-text, #475569)' }}>{subtitle}</p>}
          </div>
          {ctaText && ctaUrl && (
            <a href={ctaUrl} target={cmsConfig.cta_new_tab ? '_blank' : '_self'} rel="noopener noreferrer" className="msv-cta mx-btn mx-btn--primary self-start lg:self-end" data-testid="services-cta">
              {ctaText} <ArrowRight className="w-4 h-4" />
            </a>
          )}
        </div>

        {/* Desktop: expanding panels */}
        <div className="hidden lg:flex gap-3 h-[560px]">
          {list.map((s, i) => {
            const isOpen = i === open;
            return (
              <div key={s.id} className="msv-panel relative overflow-hidden rounded-[3px] min-w-0" data-open={isOpen ? '1' : '0'}
                style={{ flexGrow: isOpen ? 3.2 : 1, flexBasis: 0 }}
                onMouseEnter={() => setActive(i)} onFocus={() => setActive(i)} tabIndex={-1} data-testid={`service-panel-${s.id}`}>
                <PanelBg s={s} />
                {isOpen && <span className="absolute left-0 top-0 right-0 h-1" style={{ background: 'var(--color-accent, #ff9c1b)' }} />}
                <button type="button" onClick={() => setActive(i)} aria-label={tt(s.title)} aria-expanded={isOpen}
                  className="msv-vert absolute inset-0 flex items-end justify-center pb-8 text-white font-bold" style={{ fontFamily: SERIF, fontSize: '1.2rem', pointerEvents: isOpen ? 'none' : 'auto' }}>
                  <span style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', whiteSpace: 'nowrap' }}>{tt(s.title)}</span>
                </button>
                <div className="absolute inset-x-0 bottom-0 p-8 xl:p-10" style={{ pointerEvents: isOpen ? 'auto' : 'none' }}>
                  <OpenContent s={s} n={i + 1} />
                </div>
              </div>
            );
          })}
        </div>

        {/* Phones/tablets: stacked, tap to open */}
        <div className="lg:hidden flex flex-col gap-2.5">
          {list.map((s, i) => {
            const isOpen = i === open;
            return (
              <div key={s.id} className="msv-panel relative overflow-hidden rounded-[3px]" data-open={isOpen ? '1' : '0'} style={{ minHeight: isOpen ? 360 : 72 }} data-testid={`service-panel-m-${s.id}`}>
                <PanelBg s={s} />
                {isOpen && <span className="absolute left-0 top-0 right-0 h-1" style={{ background: 'var(--color-accent, #ff9c1b)' }} />}
                {!isOpen && (
                  <button type="button" onClick={() => setActive(i)} aria-expanded={false} className="relative w-full h-[72px] flex items-center justify-between px-5 text-white font-bold text-left" style={{ fontFamily: SERIF, fontSize: '1.15rem' }}>
                    {tt(s.title)} <ArrowRight className="w-4 h-4 opacity-70" />
                  </button>
                )}
                {isOpen && <div className="relative flex flex-col justify-end min-h-[360px] p-6"><OpenContent s={s} n={i + 1} /></div>}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
